import { useMemo } from 'react';
import { useGetLyricTimingQuery } from '../../../../../redux/api/fymAPI';
import { useRealtimePollInterval } from '../../../../realtime/useRealtimePollInterval';
import { pickLinesOf } from '../fullVideoRules';

/** While the lyrics sync and the realtime socket is down, the line list reads the timing every 10 s. */
const SYNC_POLL_MS = 10_000;

interface UseStudioLyricLinesProps {
  accountId: number;
  itemUuid: string;
  lyrics: string | null;
  isSyncing: boolean;
  /** True for every asset type with no line picker: the hook reads nothing. */
  skip: boolean;
}

/**
 * The lines of the lyric moment picker (design s7b): the synced timing of the song
 * (`GET .../lyric-timing`), else the lyric lines with no times. `catalog.song.updated` reads the
 * timing again when the sync ends; the hook polls only while the lyrics sync and the socket is down.
 */
export function useStudioLyricLines({ accountId, itemUuid, lyrics, isSyncing, skip }: UseStudioLyricLinesProps) {
  const pollMs = useRealtimePollInterval(SYNC_POLL_MS);
  const { data, isLoading } = useGetLyricTimingQuery(
    { accountId, itemUuid },
    { skip, pollingInterval: isSyncing ? pollMs : 0 }
  );
  const timing = data?.timing ?? null;
  const lines = useMemo(() => pickLinesOf(timing, lyrics), [lyrics, timing]);
  return { timing, lines, isLoading };
}
